/* eslint-disable camelcase */
import DataEngine from 'bdata-react-component/lib/dataEngine/DataEngine';
import MessageService from 'message-service/message-service';
import DataEvents from '../events/DataEvents';
import DistEvents from '../events/DistEvents';
import GameDayCore from '@gameday/core-legacy';
import HashEvents from '@gameday/core-legacy/lib/enum/HashEvents';
import GumboModel from '../models/GumboModel';
import MlbNativeWrapper from '@gameday/utils/MlbNativeWrapper';
import GameWorker from './GameWorker';
import accessTokenReady from '../actions/accessTokenReady';
import appConsole from 'app-console/app-console';
import connectionErrorAction from '@gameday/app/lib/store/actions/connectionErrorAction';

const { UPDATE_ALL, REFRESH_LIVE, REFRESH_PREGAME, START_HEARTBEAT, STOP_HEARTBEAT, PAUSE_HEARTBEAT, CONFIG_CHANGE, UPDATE_TFS, SWITCH_INTERNAL, SWITCH_EXTERNAL, ACCESS_TOKEN_READY, ACCESS_TOKEN_CHANGE } = DistEvents;
const { INIT_DATA_LOADED, UPDATE, GAME_SWITCH_COMPLETE, UPDATE_EXTERNAL, UPDATE_GAMEDAY_URL, CONNECTION_ERROR } = DataEvents;
const { GAME_ID_CHANGED } = HashEvents;

const gamedayDataEvents = {
    INIT_DATA_LOADED,
    UPDATE,
    UPDATE_ALL,
    REFRESH_LIVE,
    REFRESH_PREGAME,
    UPDATE_EXTERNAL,
    CONNECTION_ERROR
};

class GamedayDataEngine extends DataEngine {
    constructor (_opts) {
        super(_opts);
        const {
            dataMode,
            game,
            game_state,
            game_tab,
            lock_state,
            timecode,
            logging,
            locale,
            language = 'en',
            config: {
                env,
                disableWebSockets,
                webSocketWindow,
                forceWebSockets,
                noDelayWebSockets,
                fetchDataOnce,
                useNewCore = false,
                forceNewCore
            } = {}
        } = _opts;

        const gumboConfig = {
            dataMode,
            game,
            game_state,
            game_tab,
            locale,
            language,
            lock_state,
            tfs: timecode || undefined,
            disableWebSockets,
            webSocketWindow,
            forceWebSockets,
            noDelayWebSockets,
            logging
        };

        if (env === 'local') {
            // Flip the default logging level for all appConsole instances to
            // `logging`.
            appConsole.setLevel(logging);
        }
        // While core-legacy is active, we don't want the
        // gd5 engine to poll itself, just wait for updates.
        this.updateOpts({
            poll: false
        });

        // Create a readable promise that signals the engine is ready
        // with a game.
        // Should only be needed for the inital data call,
        // and game switching should handle itself.
        const coolNewCore = forceNewCore ?? useNewCore;

        this.gameReadyCache = new Promise((resolve) => {
            MessageService.one('init_data_loaded', function () {
                if (coolNewCore) {
                    accessTokenReady().then(token => {
                        MessageService.publish(ACCESS_TOKEN_READY, token);
                    });
                }

                MlbNativeWrapper.then((results) => {
                    const { stopTrace } = results;
                    stopTrace('gameday: gumbo fetch');
                });
                resolve();
            });
        });

        this.previousGamePk = null;
        this.previousGameState = null;
        this.forceUpdate = false;
        this.previousHideScoresSetting = false;
        this.hideScoreSetting = false;

        if (coolNewCore) {
            this.core = new GameWorker(_opts, this.store.bind(this));
        } else {
            MessageService.on(UPDATE, (data) => {
                this.processUpdates(data);
                MessageService.publish(CONNECTION_ERROR, { error: false });
            });

            MessageService.on(UPDATE_EXTERNAL, (data) => {
                //  FV only publishes when mannequin matches up with a new gumbo timecode
                //  Also make sure we're mirroring the structure
                //  from core-legacy's ApiModelItem for now
                //  (should be lighter than making a GumboModel instance twice)
                if (data.timecode) {
                    GameDayCore.syncAtTfsPoint(data.timecode);
                } else {
                    this.processUpdates({
                        api: { ...data, originalData: data },
                        newData: true
                    });
                }
            });

            MessageService.on(GAME_ID_CHANGED, (payload) => {
                GameDayCore.switchGame(payload);
            });

            MessageService.one('init_data_loaded', function () {
                MessageService.on('init_data_loaded', function () {
                    MessageService.publish(GAME_SWITCH_COMPLETE);
                });
            });

            MessageService.on(STOP_HEARTBEAT, () => {
                GameDayCore.getCurrent().stopHeartbeat();
            });

            MessageService.on(PAUSE_HEARTBEAT, () => {
                GameDayCore.getCurrent().pauseHeartbeat();
            });

            MessageService.on(SWITCH_EXTERNAL, () => {
                GameDayCore.getCurrent().pauseHeartbeat();
            });

            MessageService.on(SWITCH_INTERNAL, () => {
                GameDayCore.resetTfs();
                GameDayCore.getCurrent().startHeartbeat();
            });

            MessageService.on(START_HEARTBEAT, () => {
                GameDayCore.getCurrent().startHeartbeat();
            });

            MessageService.on(CONFIG_CHANGE, (config) => {
                GameDayCore.updateConfig(config);
            });

            MessageService.on(UPDATE_TFS, (tfs) => {
                GameDayCore.loadAtTfsPoint(tfs);
            });

            MlbNativeWrapper.then((results) => {
                const { startTrace } = results;
                startTrace('gameday: gumbo fetch');
            });

            if (dataMode === 'passive') {
                MessageService.on('init_data_loaded', (data) => {
                    this.processUpdates(data);
                });
            }

            if (fetchDataOnce) {
                gumboConfig.disableWebSockets = true;
                gumboConfig.socketMode = false;

                MessageService.on('init_data_loaded', function () {
                    GameDayCore.stopHeartbeat();
                });
            }
            GameDayCore.init(game, gumboConfig);
            this.core = GameDayCore;
            accessTokenReady().then(token => {
                MessageService.publish(ACCESS_TOKEN_CHANGE, token);
            });
        }

        MessageService.on(CONNECTION_ERROR, ({ error }) => {
            console.error('Network connection lost.');
            this.dispatch(connectionErrorAction({ error }));
        });
    }

    gameReady () {
        return Promise.resolve(this.gameReadyCache);
    }

    processUpdates (data = {}) {
        const { api } = data;
        const params = this.opts.views;

        if (api) {
            const isGameIDChanged = (this.previousGamePk !== api.gamePk && this.previousGamePk !== null);
            const apiGameState = api.gameData.status.abstractGameState;
            const isGamestateChanged = (this.forceUpdate === true);
            const hasNewData = (data.newData === true);
            const isHideScoresChanged = (this.previousHideScoresSetting !== this.hideScoreSetting);

            // Save this for later
            if (hasNewData || isGameIDChanged || isGamestateChanged || isHideScoresChanged) {
                const gumboData = new GumboModel(api?.originalData);

                this.store({
                    success: true,
                    data: gumboData,
                    source: hasNewData ? 'server' : 'cache',
                    params,
                    dataSourceName: 'gumbo'
                });
                // We've turned off polling on the data engine while core-legacy is
                // active, so lets dispatch an update signal for the data router
                // now that we know the gumbo changed.
                this.heartbeatUpdate();

                // Always pass game info to changeView and let that handle any changes necessary
                MessageService.publish(UPDATE_GAMEDAY_URL, {
                    data: {
                        prev_game_state: this.previousGameState,
                        next_game_state: apiGameState,
                        game_updated: isGameIDChanged
                    }
                });

                this.previousGamePk = api.gamePk;
                this.previousGameState = apiGameState;
            }
        } else {
            GameDayCore.logger.log('DataEngine : no new updates to report');
        }
    }
}

GamedayDataEngine.prototype.engineActions = { ...DataEngine.prototype.engineActions, ...gamedayDataEvents };

export default GamedayDataEngine;
